import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { ISMEDesignguidelines } from '../../../Models/ISMEDesignGuidelines';

// Filter interface
export interface ISMEDesignGuidelineFilter {
  industry: string;
  subIndustry: string;
  application: string;
  subApplication: string;
  processStage: string;
  productLine: string;
  alfaLavalPosition: string;
}

// Create a new empty filter
export const newSMEDesignGuidelineFilter = (): ISMEDesignGuidelineFilter => {
  return {
    industry: '',
    subIndustry: '',
    application: '',
    subApplication: '',
    processStage: '',
    productLine: '',
    alfaLavalPosition: ''
  };
};

interface SMEDesignGuidelineFilterProps {
  onChange: (filter: ISMEDesignGuidelineFilter, isFiltering: boolean) => void;
  filter: ISMEDesignGuidelineFilter;
  isFiltering: boolean;
  filteredData: ISMEDesignguidelines[];
  currentData: ISMEDesignguidelines[];
}

/**
 * SME Design Guideline Filter Component
 */
export const SMEDesignGuidelineFilter: React.FC<SMEDesignGuidelineFilterProps> = ({
  onChange,
  filter,
  isFiltering,
  filteredData,
  currentData
}) => {
  // State for filter values
  const [selectedIndustry, setSelectedIndustry] = useState<string>(filter.industry);
  const [selectedSubIndustry, setSelectedSubIndustry] = useState<string>(filter.subIndustry);
  const [selectedApplication, setSelectedApplication] = useState<string>(filter.application);
  const [selectedSubApplication, setSelectedSubApplication] = useState<string>(filter.subApplication);
  const [selectedProcessStage, setSelectedProcessStage] = useState<string>(filter.processStage);
  const [selectedProductLine, setSelectedProductLine] = useState<string>(filter.productLine);
  const [selectedPosition, setSelectedPosition] = useState<string>(filter.alfaLavalPosition);
  
  // State for dropdown options
  const [industries, setIndustries] = useState<{value: string, disabled: boolean}[]>([]);
  const [subIndustries, setSubIndustries] = useState<{value: string, disabled: boolean}[]>([]);
  const [applications, setApplications] = useState<{value: string, disabled: boolean}[]>([]);
  const [subApplications, setSubApplications] = useState<{value: string, disabled: boolean}[]>([]);
  const [processStages, setProcessStages] = useState<{value: string, disabled: boolean}[]>([]);
  const [productLines, setProductLines] = useState<{value: string, disabled: boolean}[]>([]);
  const [positions, setPositions] = useState<{value: string, disabled: boolean}[]>([]);
  
  // When currentData changes, update dropdown options
  useEffect(() => {
    updateDropdownOptions();
  }, [currentData, filter]);
  
  // When any filter selection changes, update the filter
  useEffect(() => {
    const newFilter: ISMEDesignGuidelineFilter = {
      industry: selectedIndustry,
      subIndustry: selectedSubIndustry,
      application: selectedApplication,
      subApplication: selectedSubApplication,
      processStage: selectedProcessStage,
      productLine: selectedProductLine,
      alfaLavalPosition: selectedPosition
    };
    
    const isCurrentlyFiltering = 
      selectedIndustry !== '' ||
      selectedSubIndustry !== '' ||
      selectedApplication !== '' ||
      selectedSubApplication !== '' ||
      selectedProcessStage !== '' ||
      selectedProductLine !== '' ||
      selectedPosition !== '';
    
    onChange(newFilter, isCurrentlyFiltering);
  }, [
    selectedIndustry,
    selectedSubIndustry,
    selectedApplication,
    selectedSubApplication,
    selectedProcessStage,
    selectedProductLine,
    selectedPosition
  ]);
  
  /**
   * Update dropdown options based on current data and filters
   */
  const updateDropdownOptions = () => {
    // First, get all unique values
    let applicableData = [...currentData];
    
    // Filter applicable data based on current selections
    if (selectedIndustry) {
      applicableData = applicableData.filter(item => item.industry === selectedIndustry);
    }
    if (selectedSubIndustry) {
      applicableData = applicableData.filter(item => item.subIndustry === selectedSubIndustry);
    }
    if (selectedApplication) {
      applicableData = applicableData.filter(item => item.application === selectedApplication);
    }
    if (selectedSubApplication) {
      applicableData = applicableData.filter(item => item.subApplication === selectedSubApplication);
    }
    if (selectedProcessStage) {
      applicableData = applicableData.filter(item => item.processStage === selectedProcessStage);
    }
    if (selectedProductLine) {
      applicableData = applicableData.filter(item => item.productLine === selectedProductLine);
    }
    
    // Get unique values and create options
    const uniqueIndustries = Array.from(new Set(currentData.map(item => item.industry)));
    const uniqueSubIndustries = Array.from(new Set(currentData.map(item => item.subIndustry)));
    const uniqueApplications = Array.from(new Set(currentData.map(item => item.application)));
    const uniqueSubApplications = Array.from(new Set(currentData.map(item => item.subApplication)));
    const uniqueProcessStages = Array.from(new Set(currentData.map(item => item.processStage)));
    const uniqueProductLines = Array.from(new Set(currentData.map(item => item.productLine)));
    const uniquePositions = Array.from(new Set(currentData.map(item => item.alfaLavalPosition)));
    
    // Create industry options
    setIndustries(
      uniqueIndustries.map(industry => ({
        value: industry,
        disabled: false
      }))
    );
    
    // Create sub-industry options
    setSubIndustries(
      uniqueSubIndustries.map(subIndustry => ({
        value: subIndustry,
        disabled: selectedIndustry !== '' && !applicableData.some(item => item.subIndustry === subIndustry)
      }))
    );
    
    // Create application options
    setApplications(
      uniqueApplications.map(application => ({
        value: application,
        disabled: (selectedIndustry !== '' || selectedSubIndustry !== '') && 
                  !applicableData.some(item => item.application === application)
      }))
    );
    
    // Create sub-application options
    setSubApplications(
      uniqueSubApplications.map(subApplication => ({
        value: subApplication,
        disabled: (selectedIndustry !== '' || selectedSubIndustry !== '' || selectedApplication !== '') && 
                  !applicableData.some(item => item.subApplication === subApplication)
      }))
    );
    
    // Create process stage options
    setProcessStages(
      uniqueProcessStages.map(processStage => ({
        value: processStage,
        disabled: (selectedIndustry !== '' || selectedSubIndustry !== '' || 
                  selectedApplication !== '' || selectedSubApplication !== '') && 
                  !applicableData.some(item => item.processStage === processStage)
      }))
    );
    
    // Create product line options
    setProductLines(
      uniqueProductLines.map(productLine => ({
        value: productLine,
        disabled: (selectedIndustry !== '' || selectedSubIndustry !== '' || 
                  selectedApplication !== '' || selectedSubApplication !== '' || 
                  selectedProcessStage !== '') && 
                  !applicableData.some(item => item.productLine === productLine)
      }))
    );
    
    // Create position options
    setPositions(
      uniquePositions.map(position => ({
        value: position,
        disabled: (selectedIndustry !== '' || selectedSubIndustry !== '' || 
                  selectedApplication !== '' || selectedSubApplication !== '' || 
                  selectedProcessStage !== '' || selectedProductLine !== '') && 
                  !applicableData.some(item => item.alfaLavalPosition === position)
      }))
    );
  };
  
  /**
   * Handle select change
   */
  const handleSelectChange = (value: string, field: string) => {
    switch (field) {
      case 'industry':
        setSelectedIndustry(value);
        setSelectedSubIndustry('');
        setSelectedApplication('');
        setSelectedSubApplication('');
        setSelectedProductLine('');
        setSelectedProcessStage('');
        setSelectedPosition('');
        break;
      case 'subIndustry':
        setSelectedSubIndustry(value);
        setSelectedApplication('');
        setSelectedSubApplication('');
        setSelectedProductLine('');
        setSelectedProcessStage('');
        setSelectedPosition('');
        break;
      case 'application':
        setSelectedApplication(value);
        setSelectedSubApplication('');
        setSelectedProductLine('');
        setSelectedProcessStage('');
        setSelectedPosition('');
        break;
      case 'subApplication':
        setSelectedSubApplication(value);
        setSelectedProductLine('');
        setSelectedProcessStage('');
        setSelectedPosition('');
        break;
      case 'processStage':
        setSelectedProcessStage(value);
        setSelectedProductLine('');
        setSelectedPosition('');
        break;
      case 'productLine':
        setSelectedProductLine(value);
        setSelectedPosition('');
        break;
      case 'position':
        setSelectedPosition(value);
        break;
      default:
        break;
    }
  };

  // Filter form styles
  const filterStyles = {
    filterContainer: {
      width: '100%',
      p: 0
    },
    formRow: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      flexWrap: 'wrap',
      mb: 1,
      gap: 2
    },
    formControl: {
      flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 30%' },
      minWidth: { xs: '100%', sm: '220px' },
      height: '100%'
    },
    select: {
      height: '40px',
      '& .MuiSelect-select': {
        padding: '8px 14px'
      }
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
        Filter Design Guidelines
      </Typography>
      
      <Box sx={filterStyles.filterContainer}>
        {/* First row of filters */}
        <Box sx={filterStyles.formRow}>
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="industry-label">Industry</InputLabel>
            <Select
              labelId="industry-label"
              value={selectedIndustry}
              onChange={(e) => handleSelectChange(e.target.value as string, 'industry')}
              label="Industry"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {industries.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="subIndustry-label">Sub Industry</InputLabel>
            <Select
              labelId="subIndustry-label"
              value={selectedSubIndustry}
              onChange={(e) => handleSelectChange(e.target.value as string, 'subIndustry')}
              label="Sub Industry"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {subIndustries.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="application-label">Application</InputLabel>
            <Select
              labelId="application-label"
              value={selectedApplication}
              onChange={(e) => handleSelectChange(e.target.value as string, 'application')}
              label="Application"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {applications.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Second row of filters */}
        <Box sx={filterStyles.formRow}>
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="subApplication-label">Sub Application</InputLabel>
            <Select
              labelId="subApplication-label"
              value={selectedSubApplication}
              onChange={(e) => handleSelectChange(e.target.value as string, 'subApplication')}
              label="Sub Application"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {subApplications.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="processStage-label">Process Stage</InputLabel>
            <Select
              labelId="processStage-label"
              value={selectedProcessStage}
              onChange={(e) => handleSelectChange(e.target.value as string, 'processStage')}
              label="Process Stage"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {processStages.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="productLine-label">Product Line</InputLabel>
            <Select
              labelId="productLine-label"
              value={selectedProductLine}
              onChange={(e) => handleSelectChange(e.target.value as string, 'productLine')}
              label="Product Line"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {productLines.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Third row of filters */}
        <Box sx={filterStyles.formRow}>
          <FormControl 
            fullWidth 
            size="small" 
            sx={filterStyles.formControl}
          >
            <InputLabel id="position-label">Alfa Laval Position</InputLabel>
            <Select
              labelId="position-label"
              value={selectedPosition}
              onChange={(e) => handleSelectChange(e.target.value as string, 'position')}
              label="Alfa Laval Position"
              sx={filterStyles.select}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {positions.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  disabled={option.disabled}
                >
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default SMEDesignGuidelineFilter; 